import React from 'react';
import type { Participation } from 'app/types';
import { useDialog } from 'app/hooks';

export const useParticipationsState = (participations: Participation[]) => {
  const [participationToEdit, setParticipationToEdit] = React.useState<Participation | null>(null);
  const [participationToDelete, setParticipationToDelete] = React.useState<Participation | null>(null);
  const [expanded, setExpanded] = React.useState<boolean[]>([]);

  React.useEffect(() => {
    setExpanded(participations.map(() => false));
  }, [participations]);

  const { dialogState: formDialogState, openDialog: openFormDialog, closeDialog: closeFormDialog } = useDialog();
  const { dialogState: deleteDialogState, openDialog: openDeleteDialog, closeDialog: closeDeleteDialog } = useDialog();

  const onOpenFormDialog = React.useCallback(
    (participation?: Participation) => {
      setParticipationToEdit(participation || null);
      openFormDialog();
    },
    [openFormDialog],
  );

  const onCloseFormDialog = React.useCallback(() => {
    setParticipationToEdit(null);
    closeFormDialog();
  }, [closeFormDialog]);

  const onOpenDeleteDialog = React.useCallback(
    (participation: Participation) => {
      setParticipationToDelete(participation);
      openDeleteDialog();
    },
    [openDeleteDialog],
  );

  const onCloseDeleteDialog = React.useCallback(() => {
    setParticipationToDelete(null);
    closeDeleteDialog();
  }, [closeDeleteDialog]);

  const toggleDetailsPanel = React.useCallback(
    ({ id, expanded: currentExpanded }: { id: number; expanded: boolean }) => {
      setExpanded(prev => {
        const next = new Array(prev.length).fill(false);
        next[id] = !currentExpanded;
        return next;
      });
    },
    [],
  );

  return {
    formDialogState,
    deleteDialogState,
    participationToEdit,
    participationToDelete,
    expanded,
    onOpenFormDialog,
    onCloseFormDialog,
    onOpenDeleteDialog,
    onCloseDeleteDialog,
    toggleDetailsPanel,
  };
};
